import {
  type ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { formatDistanceToNow } from 'date-fns'
import {
  ArrowDownSquare,
  History,
  Info,
  Play,
  RefreshCw
} from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { adminMedicalPlanDataFiles, adminMedicalPlanIngestions } from 'rails_routes'
import { Button, Checkbox, Tooltip, TooltipProvider } from 'slab'
import {
  useGetAdminMedicalPlanData,
  usePostAdminMedicalPlanDataRefreshStateStatusReport,
  usePostAdminMedicalPlanDataRunIngestion
} from 'utils/web_api'
import { type PostAdminMedicalPlanDataRunIngestionStatesItem } from 'utils/web_api.schemas'
import { useWindowLocation } from 'utils/windowLocation'

import { StateStatusBadge } from './StateStatusBadge'
import { StateStatusLegend } from './StateStatusLegend'
import LoadingSpinnerCentered from '../../reusable/LoadingSpinnerCentered'
import TanstackTable from '../../reusable/table/TanstackTable'

interface StateData {
  name: string
  abbreviation: string
  lastIngestionTime?: string | null
  s3Available?: boolean
  s3LastModified?: string | null
  needsReingestion?: boolean
  isUpToDate?: boolean
  selected?: boolean
}

interface MedicalPlanDataTableProps {
  initiallySelectedPlanYear: number;
  planYears: number[];
}

export const MedicalPlanDataTable: React.FC<MedicalPlanDataTableProps> = ({
  initiallySelectedPlanYear,
  planYears,
}) => {
  const { assign } = useWindowLocation()

  const [selectedYear, setSelectedYear] = useState(initiallySelectedPlanYear)

  const [selectedStates, setSelectedStates] = useState<Set<string>>(new Set())
  const [allStatesSelected, setAllStatesSelected] = useState(false)

  const { data, isLoading, refetch } = useGetAdminMedicalPlanData({ year: selectedYear })
  const { mutate: checkS3Availability, isPending: isCheckingS3 } = usePostAdminMedicalPlanDataRefreshStateStatusReport()
  const { mutate: runIngestion, isPending: isRunningIngestion } = usePostAdminMedicalPlanDataRunIngestion()

  const statesData = useMemo<StateData[]>(() => {
    if (!data || !data.states) return []

    return data.states.map((state: any) => {
      const needsReingestion = state.last_ingestion_date && state.needs_ingestion
      return {
        name: state.name,
        abbreviation: state.abbreviation,
        lastIngestionTime: state.last_ingestion_date,
        s3Available: state.s3_file_available,
        s3LastModified: state.s3_file_last_modified,
        isUpToDate: state.is_up_to_date,
        needsReingestion,
      }
    })
  }, [data])

  useEffect(() => {
    if (selectedStates.size === statesData.length && selectedStates.size > 0) {
      setAllStatesSelected(true)
    }
  }, [selectedStates, statesData])

  const columnHelper = createColumnHelper<StateData>()

  const columns = useMemo<ColumnDef<StateData, any>[]>(() => {
    const baseColumns = [
      columnHelper.display({
        id: 'select',
        header: () => (
          <div className='flex justify-center'>
            <Checkbox
              checked={allStatesSelected}
              indeterminate={selectedStates.size > 0 && selectedStates.size < statesData.length}
              onChange={(e) => {
                setAllStatesSelected(e.target.checked)
                if (e.target.checked) {
                  setSelectedStates(new Set(statesData.map(s => s.abbreviation)))
                } else {
                  setSelectedStates(new Set())
                }
              }}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className='flex justify-center'>
            <Checkbox
              checked={selectedStates.has(row.original.abbreviation)}
              onChange={(e) => {
                const newSelected = new Set(selectedStates)
                if (e.target.checked) {
                  newSelected.add(row.original.abbreviation)
                } else {
                  newSelected.delete(row.original.abbreviation)
                }

                setSelectedStates(newSelected)
                setAllStatesSelected(false)
              }}
            />
          </div>
        ),
      }),
      columnHelper.accessor('name', {
        header: 'State',
        cell: ({ row }) => (
          <span className='font-medium text-gray-900'>
            {row.original.name}
          </span>
        ),
      }),
      columnHelper.accessor('lastIngestionTime', {
        header: () => <div className='text-center'>Last Ingestion ({selectedYear})</div>,
        cell: ({ getValue }) => {
          const value = getValue()
          return (
            <div className='text-center text-sm text-gray-500'>
              {value
                ? (
                  <span title={new Date(value).toLocaleString()}>
                    {formatDistanceToNow(new Date(value), { addSuffix: true })}
                  </span>
                  )
                : (
                  <span className='text-gray-400'>Never</span>
                  )}
            </div>
          )
        },
      }),
      columnHelper.display({
        id: 's3Status',
        header: () => (
          <div className='text-center flex items-center justify-center gap-1'>
            <span>Ideon S3 Status ({selectedYear})</span>
            <Tooltip content={<StateStatusLegend />} side='top'>
              <Info className='w-4 h-4 text-gray-600 hover:text-gray-900 cursor-help' />
            </Tooltip>
          </div>
        ),
        cell: ({ row }) => (
          <div className='text-center'>
            <StateStatusBadge
              available={row.original.s3Available}
              lastModified={row.original.s3LastModified}
              needsReingestion={row.original.needsReingestion}
              isUpToDate={row.original.isUpToDate}
            />
          </div>
        ),
      })
    ]

    return baseColumns
  }, [selectedYear, selectedStates, allStatesSelected, statesData, checkS3Availability, refetch])

  const table = useReactTable({
    data: statesData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    initialState: {
      pagination: {
        pageSize: 51, // 50 US states + DC
      },
    },
  })

  if (isLoading) {
    return (
      <div className='flex flex-col justify-center items-center h-64'>
        <div className='mb-8 text-gray-500'>
          {`Loading medical plan data for ${selectedYear}...`}
        </div>
        <LoadingSpinnerCentered height='50px' width='50px' />
      </div>
    )
  }

  const handleStateOperations = (fn: typeof runIngestion | typeof checkS3Availability, actionDescription: string) => {
    const states = allStatesSelected ? [] : Array.from(selectedStates)
    fn(
      {
        data: {
          year: selectedYear,
          states: states as PostAdminMedicalPlanDataRunIngestionStatesItem[],
          all_states: allStatesSelected
        }
      },
      {
        onSuccess: (data: any) => {
          setSelectedStates(new Set())
          setAllStatesSelected(false)
          refetch()

          const message = data?.message || `${actionDescription} completed successfully`
          toast.success(message)
        },
        onError: (error: any) => {
          const errorMessage = error?.response?.data?.error ||
                              error?.message ||
                              `Failed to complete ${actionDescription}`
          toast.error(`${actionDescription} Failed: ${errorMessage}`)
        }
      }
    )
  }

  const handleRunSelectedJobs = () => handleStateOperations(runIngestion, 'Scheduling of Ingestion')
  const handleCheckSelectedS3Status = () => handleStateOperations(checkS3Availability, 'Checking Ideon Status')

  const handleClickYear = (year: number) => () => {
    setSelectedStates(new Set())
    setAllStatesSelected(false)

    const url = new URL(window.location.href)
    url.searchParams.set('year', String(year))
    window.history.pushState({}, '', url)
    setSelectedYear(year)
  }

  return (
    <TooltipProvider>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex justify-between items-center'>
          <div className='flex space-x-2 items-center'>
            <ArrowDownSquare className='w-8 h-8' />
            <h1 className='text-2xl font-bold'>Medical plan data</h1>
          </div>

          {/* Quick action button */}
          <Button
            variant='secondary'
            leftIcon={History}
            onClick={() => { assign(adminMedicalPlanIngestions.index()) }}
          >
            View Ingestion Job History
          </Button>
        </div>

        {/* Info text with navigation */}
        <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
          <div className='flex items-start gap-3'>
            <Info className='w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0' />
            <div className='space-y-2 text-sm'>
              <p className='text-gray-700'>
                <span className='font-medium'>Upload MA files:</span> To upload files for MA plan data ingestion, visit the{' '}
                <a href={adminMedicalPlanDataFiles.index()} className='text-blue-600 hover:text-blue-800 underline'>
                  Medical plan data files page
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className='flex items-center justify-between'>
          {/* Year selector */}
          <div className='flex items-center space-x-4'>
            <span className='text-sm font-medium text-gray-700'>Plan year:</span>
            <div className='flex bg-gray-100 p-1 rounded-lg'>
              {planYears.map((year) => (
                <Button
                  key={year}
                  onClick={handleClickYear(year)}
                  size='small'
                  variant={year === selectedYear ? 'secondary' : 'tertiary-deemphasized'}
                >
                  {year}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className='flex space-x-3'>
          <Button
            variant='primary'
            onClick={handleRunSelectedJobs}
            disabled={selectedStates.size === 0 && !allStatesSelected}
            leftIcon={Play}
          >
            {isRunningIngestion ? 'Running...' : 'Run ingestion'}
          </Button>
          <Button
            variant='secondary'
            onClick={handleCheckSelectedS3Status}
            leftIcon={RefreshCw}
            disabled={selectedStates.size === 0 && !allStatesSelected}
          >
            {isCheckingS3 ? 'Checking...' : 'Check Ideon status'}
          </Button>
        </div>

        {/* Table */}
        <TanstackTable
          table={table}
          isLoading={isLoading}
          paginationLabelSingular='state'
          showPageSelectionIfSinglePage
          rowClasses='text-gray-600 text-sm'
        />
      </div>
    </TooltipProvider>
  )
}

export default MedicalPlanDataTable
